import { Component, type ErrorInfo, type ReactNode } from "react";

/**
 * Props accepted by the ErrorBoundary.
 * - fallback: Optional UI shown when an error is caught
 * - onError: Optional callback for logging (Sentry, Datadog, etc.)
 */
export interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

/**
 * Internal state used by the ErrorBoundary
 */
interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * ErrorBoundary
 *
 * Catches JavaScript errors that occur during:
 * - Rendering
 * - Lifecycle methods
 * - Constructors of child components
 *
 * It does NOT catch:
 * - Errors in event handlers
 * - Errors in async code (Promises, useEffect)
 * - Errors outside the React tree
 */
export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = {
    hasError: false,
    error: null,
  };

  /**
   * Updates state so the next render shows the fallback UI.
   * This method must be static.
   */
  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      error,
    };
  }

  /**
   * Called after an error is caught.
   * Ideal place for logging and monitoring.
   */
  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Default logging (always safe)
    console.error("ErrorBoundary caught an error:", error);
    console.error(errorInfo.componentStack);

    // Optional external logging
    this.props.onError?.(error, errorInfo);
  }

  /**
   * Allows users to retry rendering the children
   * without refreshing the page.
   */
  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
    });
  };

  render() {
    const { hasError, error } = this.state;

    if (hasError) {
      // If a custom fallback is provided, render it
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default fallback: inline styles only so it never throws (no Tailwind,
      // no app components, no theme). Works even when rendered at root.
      return (
        <div
          style={{
            minHeight: 280,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 16,
            padding: 24,
            textAlign: "center",
            fontFamily: "system-ui, sans-serif",
          }}
        >
          <h2 style={{ fontSize: 18, fontWeight: 600, margin: 0, color: "#1a1a1a" }}>
            Something went wrong
          </h2>
          <p style={{ fontSize: 14, color: "#666", maxWidth: 400, margin: 0 }}>
            This section could not be loaded. Please try again.
          </p>
          {process.env.NODE_ENV === "development" && error && (
            <pre
              style={{
                marginTop: 12,
                padding: 12,
                maxHeight: 96,
                overflow: "auto",
                background: "#f5f5f5",
                borderRadius: 6,
                fontSize: 12,
                textAlign: "left",
                color: "#333",
              }}
            >
              {error.message}
            </pre>
          )}
          <button
            type="button"
            onClick={this.handleRetry}
            style={{
              marginTop: 16,
              padding: "8px 16px",
              borderRadius: 6,
              fontSize: 14,
              fontWeight: 500,
              backgroundColor: "#a855f7",
              color: "white",
              border: "none",
              cursor: "pointer",
            }}
          >
            Try again
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
